import React, { useState } from 'react';
import { FiCoffee, FiClock, FiCheckCircle, FiUser, FiPhone, FiMail, FiBriefcase, FiMapPin, FiTruck } from 'react-icons/fi';
import api from '../../services/api';

type Tipo = 'restaurante' | 'cliente' | 'entregador';

const FoodComingSoon: React.FC = () => {
  const [tipo, setTipo] = useState<Tipo>('restaurante');
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', negocio: '', cidade: '', veiculo: '', mensagem: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post('/public/food-adesao', { tipo, ...form });
      if (r.data?.success) {
        setSuccess(true);
        setForm({ nome: '', email: '', telefone: '', negocio: '', cidade: '', veiculo: '', mensagem: '' });
      } else {
        setError(r.data?.error || 'Erro ao enviar pedido');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao enviar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pagali-primary/5 via-white to-pagali-primary/10">
      {/* Banner CTA */}
      <div className="bg-gradient-to-r from-pagali-primary to-purple-600 text-white">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-16 flex flex-col md:flex-row items-center gap-8">
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <FiCoffee className="text-5xl" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs font-semibold mb-3">
              <FiClock /> BREVEMENTE
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Pagali Food está a chegar</h1>
            <p className="text-white/90 text-base md:text-lg">
              A nova plataforma de delivery de Cabo Verde. Junta-te aos primeiros restaurantes e utilizadores.
            </p>
          </div>
          <a href="#adesao" className="bg-white text-pagali-primary font-bold px-6 py-3 rounded-xl shadow-lg hover:scale-105 transition-transform whitespace-nowrap">
            Pedir adesão
          </a>
        </div>
      </div>

      {/* Benefícios */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: <FiBriefcase />, t: 'Para Restaurantes', d: 'Expande o teu negócio com entregas em toda a ilha e receba pagamentos integrados.' },
            { icon: <FiUser />, t: 'Para Clientes', d: 'Encomenda comida dos teus restaurantes favoritos com entrega rápida e segura.' },
            { icon: <FiTruck />, t: 'Para Entregadores', d: 'Ganha dinheiro a fazer entregas no teu horário. Regista a tua moto ou veículo e começa a trabalhar.' },
            { icon: <FiCheckCircle />, t: 'Lançamento Prioritário', d: 'Os primeiros a aderir têm condições especiais e destaque na plataforma.' },
          ].map((b, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-lg bg-pagali-primary/10 text-pagali-primary flex items-center justify-center text-xl mb-3">{b.icon}</div>
              <h3 className="font-bold text-gray-800 mb-1">{b.t}</h3>
              <p className="text-gray-600 text-sm">{b.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Formulário de adesão */}
      <div id="adesao" className="max-w-2xl mx-auto px-6 pb-16">
        <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-gray-100">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                <FiCheckCircle className="text-4xl text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Pedido registado!</h2>
              <p className="text-gray-600 mb-6">A equipa Pagali entrará em contacto em breve.</p>
              <button onClick={() => setSuccess(false)} className="text-pagali-primary font-semibold hover:underline">
                Enviar outro pedido
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Pedir adesão</h2>
              <p className="text-gray-500 text-center mb-6">Preenche os teus dados e entramos em contacto.</p>

              {/* Toggle tipo */}
              <div className="flex gap-2 bg-gray-100 rounded-xl p-1 mb-6">
                {(['restaurante', 'cliente', 'entregador'] as Tipo[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTipo(t)}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-colors ${
                      tipo === t ? 'bg-white text-pagali-primary shadow' : 'text-gray-500'
                    }`}
                  >
                    {t === 'restaurante' ? 'Sou Restaurante' : t === 'cliente' ? 'Sou Cliente' : 'Sou Entregador'}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-4">
                <Field icon={<FiUser />} placeholder="Nome completo *" value={form.nome}
                  onChange={v => setForm({ ...form, nome: v })} required />
                <div className="grid md:grid-cols-2 gap-4">
                  <Field icon={<FiPhone />} placeholder="Telefone *" type="tel" value={form.telefone}
                    onChange={v => setForm({ ...form, telefone: v })} required />
                  <Field icon={<FiMail />} placeholder="Email *" type="email" value={form.email}
                    onChange={v => setForm({ ...form, email: v })} required />
                </div>
                {tipo === 'restaurante' && (
                  <Field icon={<FiBriefcase />} placeholder="Nome do restaurante" value={form.negocio}
                    onChange={v => setForm({ ...form, negocio: v })} />
                )}
                {tipo === 'entregador' && (
                  <Field icon={<FiTruck />} placeholder="Tipo de veículo (moto, bicicleta, carro...)" value={form.veiculo}
                    onChange={v => setForm({ ...form, veiculo: v })} />
                )}
                <Field icon={<FiMapPin />} placeholder="Cidade / Ilha" value={form.cidade}
                  onChange={v => setForm({ ...form, cidade: v })} />
                <textarea
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-pagali-primary focus:border-transparent outline-none transition-all resize-none"
                  rows={3}
                  placeholder={
                    tipo === 'restaurante' ? 'Tipo de cozinha, horário, outras informações...' :
                    tipo === 'entregador' ? 'Disponibilidade horária, zona de entrega preferida...' :
                    'Alguma preferência ou comentário?'
                  }
                  value={form.mensagem}
                  onChange={e => setForm({ ...form, mensagem: e.target.value })}
                />

                {error && (
                  <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-pagali-primary to-purple-600 text-white font-bold py-3.5 rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {submitting ? 'A enviar...' : 'Enviar pedido'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{
  icon: React.ReactNode;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}> = ({ icon, placeholder, value, onChange, type = 'text', required }) => (
  <div className="relative">
    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>
    <input
      type={type}
      required={required}
      className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-pagali-primary focus:border-transparent outline-none transition-all"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

export default FoodComingSoon;
